import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Combobox, Input } from '@wonderschool/common-base-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

export function ParentsListingTable({ state, setState }) {
  const { t } = useTranslation();
  const selectRoomsList = createSelector([(state) => state.rooms], (rooms) => rooms?.list ?? []);

  const rooms = useSelector(selectRoomsList);

  const options = useMemo(() => {
    if (!rooms?.length) return [];

    const allRooms = rooms
      ?.filter((room: { isArchived: boolean }) => !room.isArchived)
      .map((room: { id: any; name: any }) => ({
        id: room.id,
        name: room.name,
        value: room.id,
      }));

    return allRooms;
  }, [rooms]);

  return (
    <div className="parentsListingFilter px-8">
      <div className="w-full gap-2 sm:flex">
        <div className="mb-4 grow sm:mb-0">
          <Combobox
            placeholder={t('familyDirectory.filters.roomLabel')}
            options={options}
            onChange={({ value }) => {
              setState((prev: { filters: any }) => ({
                ...prev,
                filters: { ...prev.filters, room: value },
              }));
            }}
            label={t('familyDirectory.filters.roomLabel')}
            value={state.filters?.room || ''}
            wrapperClasses="my-auto flex-grow"
            data-testid="room-filter"
          />
        </div>
        <div className="mb-4 grow sm:mb-0">
          <Combobox
            placeholder={t('familyDirectory.filters.allRelationships')}
            options={[
              { id: 'parent', value: 'parent', name: t('familyDirectory.filters.parentGuardian') },
              { id: 'other', value: 'other', name: t('familyDirectory.filters.otherRelationship') },
            ]}
            onChange={({ value }) => {
              setState((prev: { filters: any }) => ({
                ...prev,
                filters: { ...prev.filters, relationship: value },
              }));
            }}
            label={t('common.relationship')}
            value={state.filters?.relationship || ''}
            wrapperClasses="my-auto flex-grow"
            data-testid="relationship-filter"
          />
        </div>
        <div className="mb-4 grow sm:mb-0">
          <Combobox
            placeholder={t('familyDirectory.filters.noAutopayPreference')}
            options={[
              { id: 'yes', value: 'yes', name: t('common.yes') },
              { id: 'no', value: 'no', name: t('common.no') },
            ]}
            onChange={({ value }) => {
              setState((prev: { filters: any }) => ({
                ...prev,
                filters: { ...prev.filters, autopay: value },
              }));
            }}
            label={`${t('familyDirectory.filters.autopayLabel')} (${t('common.primaryPayer')})`}
            value={state.filters?.autopay || ''}
            wrapperClasses="my-auto flex-grow"
            data-testid="autopay-filter"
          />
        </div>
        <div className="mb-4 grow sm:mb-0">
          <Combobox
            placeholder={t('familyDirectory.filters.allInviteStatuses')}
            options={[
              { id: 'verified', value: 'verified', name: t('familyDirectory.filters.inviteAccepted') },
              { id: 'invited', value: 'invited', name: t('familyDirectory.filters.invited') },
              { id: 'new', value: 'new', name: t('familyDirectory.filters.notInvited') },
            ]}
            onChange={({ value }) => {
              setState((prev: { filters: any }) => ({
                ...prev,
                filters: { ...prev.filters, invite: value },
              }));
            }}
            label={t('familyDirectory.filters.inviteStatusLabel')}
            value={state.filters?.invite || ''}
            wrapperClasses="my-auto flex-grow"
            data-testid="invite-status-filter"
          />
        </div>
      </div>
      <div className="mb-4 mt-2 sm:mb-0">
        <Input
          placeholder={t('familyDirectory.filters.searchPlaceholder')}
          onChange={(e) =>
            setState({
              ...state,
              searchText: e.currentTarget.value,
            })
          }
          preIcon={<MagnifyingGlassIcon className="text-grey size-6" />}
          data-testid="parents-search-input-field"
        />
      </div>
    </div>
  );
}

export default ParentsListingTable;
